import { FunctionComponent, PropsWithChildren } from 'react';
import { PriceCondition } from '@/models/conditionLink';
import { Stack, SvgIcon, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { BoldLabel } from '@/components/Layout/Textual';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import {
    ConditionInfoContainer,
    ConditionPreviewContainer,
    FullColumnTile,
    IconButtonContainer,
    IconButtonTop,
    SortNumberContainer,
    SplitIconContainer,
} from './ConditionList';

type PriceConditionPreviewProps = {
    condition: PriceCondition;
    onCopy: (template: PriceCondition) => void;
    onUpdate: (condition: PriceCondition) => void;
    onDelete: (condition: PriceCondition) => void;
    onPriorityChange: (
        condition: PriceCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    isLast: boolean;
    isFirst: boolean;
    position: number;
    disableDrag?: boolean;
};

export const PriceConditionPreview: FunctionComponent<
    PropsWithChildren<PriceConditionPreviewProps>
> = ({
    condition,
    onPriorityChange,
    onUpdate,
    onCopy,
    onDelete,
    isFirst,
    isLast,
    position,
    disableDrag = false,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: condition.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging && {
            position: 'relative',
            zIndex: 10000,
        }),
    };

    return (
        <ConditionPreviewContainer
            sx={{
                backgroundColor: '#fff',
                zIndex: 10,

                ...style,
            }}
            ref={setNodeRef}
        >
            <SortNumberContainer>
                <BoldLabel>{position}.</BoldLabel>
            </SortNumberContainer>
            <ConditionInfoContainer>
                <Stack
                    spacing={1}
                    sx={{
                        width: '50%',
                    }}
                >
                    <Typography>
                        <BoldLabel>Interner Name:&nbsp;</BoldLabel>
                        {condition.name}
                    </Typography>

                    {(condition.dayContingentFrom !== null ||
                        condition.dayContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>
                                Kontingent Verkaufsdatum:&nbsp;
                            </BoldLabel>
                            {condition.dayContingentFrom !== null &&
                                `von ${condition.dayContingentFrom}`}{' '}
                            {condition.dayContingentTo !== null &&
                                `bis ${condition.dayContingentTo}`}
                        </Typography>
                    )}

                    {(condition.startDateContingentFrom !== null ||
                        condition.startDateContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>Kontingent Startdatum:&nbsp;</BoldLabel>
                            {condition.startDateContingentFrom !== null &&
                                `von ${condition.startDateContingentFrom}`}{' '}
                            {condition.startDateContingentTo !== null &&
                                `bis ${condition.startDateContingentTo}`}
                        </Typography>
                    )}

                    {(condition.conditionContingentFrom !== null ||
                        condition.conditionContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>Kontingent Regel:&nbsp;</BoldLabel>
                            {condition.conditionContingentFrom !== null &&
                                `von ${condition.conditionContingentFrom}`}{' '}
                            {condition.conditionContingentTo !== null &&
                                `bis ${condition.conditionContingentTo}`}
                        </Typography>
                    )}

                    {(condition.validityPeriodContingentFrom !== null ||
                        condition.validityPeriodContingentTo !== null) && (
                        <Typography>
                            <BoldLabel>
                                Kontingent Gültigkeitszeitraum:&nbsp;
                            </BoldLabel>
                            {condition.validityPeriodContingentFrom !== null &&
                                `von ${condition.validityPeriodContingentFrom}`}{' '}
                            {condition.validityPeriodContingentTo !== null &&
                                `bis ${condition.validityPeriodContingentTo}`}
                        </Typography>
                    )}
                </Stack>
                <Stack
                    spacing={1}
                    sx={{
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}
                >
                    <Typography>
                        <BoldLabel>Stornierbar:</BoldLabel>{' '}
                        {condition.blockCancellation ? 'Nein' : 'Ja'}
                    </Typography>

                    <Typography>
                        <BoldLabel>Produktspezifische Zählung:</BoldLabel>{' '}
                        {condition.contingentsProductIdSpecific ? 'Ja' : 'Nein'}
                    </Typography>

                    {(!!condition.startDateValidFrom ||
                        !!condition.startDateValidTo) && (
                        <Typography>
                            <BoldLabel>Gültigkeitszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {(!!condition.validTo || !!condition.validFrom) && (
                        <Typography>
                            <BoldLabel>Verkaufszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {!!condition.personTypeIds.length && (
                        <Typography>
                            <BoldLabel>Personentyp(en):</BoldLabel> konfiguriert
                        </Typography>
                    )}
                </Stack>
            </ConditionInfoContainer>
            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => onUpdate(condition)}
                    disableElevation
                >
                    <EditIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onCopy({
                            ...condition,
                            id: '',
                            name: `${condition.name} (kopie)`,
                        });
                    }}
                    disableElevation
                >
                    <ContentCopyIcon />
                </IconButtonContainer>
            </SplitIconContainer>

            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => {
                        onPriorityChange(condition, 'increase');
                    }}
                    disabled={isFirst}
                    disableElevation
                >
                    <KeyboardArrowUpIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onPriorityChange(condition, 'decrease');
                    }}
                    disabled={isLast}
                    disableElevation
                >
                    <KeyboardArrowDownIcon />
                </IconButtonContainer>
            </SplitIconContainer>
            <FullColumnTile onClick={() => onDelete(condition)}>
                <SvgIcon>
                    <DeleteIcon />
                </SvgIcon>
            </FullColumnTile>
            <FullColumnTile
                {...attributes}
                {...listeners}
                disabled={disableDrag}
            >
                <SvgIcon>
                    <ControlCameraIcon />
                </SvgIcon>
            </FullColumnTile>
        </ConditionPreviewContainer>
    );
};
